import React, { useState } from 'react';
import {
  Grid, TextField, CircularProgress, Typography, Box,
} from '@mui/material';
import { ALPHANUMERIC_RE, getPersonaFieldError, PersonaFormFields } from './EnvioNacionalModal.utils';
import { IndicativoPaisField } from './IndicativoPaisField';

export interface PersonaFormLabels {
  titulo: string;
  nombre: string;
  colonia: string;
}

export type TouchedFields = Partial<Record<keyof PersonaFormFields, boolean>>;

export interface PersonaFormProps {
  datos: PersonaFormFields;
  onChange: (field: keyof PersonaFormFields, value: string) => void;
  labels: PersonaFormLabels;
  isLoadingMunicipio?: boolean;
  errorCodigoPostal?: string | null;
  touched?: TouchedFields;
  onMarkTouched?: (field: keyof PersonaFormFields) => void;
}

export const PersonaForm: React.FC<PersonaFormProps> = ({
  datos,
  onChange,
  labels,
  isLoadingMunicipio = false,
  errorCodigoPostal = null,
  touched: touchedProp,
  onMarkTouched,
}) => {
  const [touchedLocal, setTouchedLocal] = useState<TouchedFields>({});

  const touched = touchedProp ?? touchedLocal;

  const markTouched = (field: keyof PersonaFormFields) => {
    if (onMarkTouched) {
      onMarkTouched(field);
    } else {
      setTouchedLocal(prev => ({ ...prev, [field]: true }));
    }
  };

  const fieldError = (field: keyof PersonaFormFields): string => {
    if (field === 'codigoPostal' && errorCodigoPostal && datos.codigoPostal) {
      return errorCodigoPostal;
    }
    return touched[field] ? getPersonaFieldError(field, datos, errorCodigoPostal) : '';
  };

  const handleNombre = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange('nombre', e.target.value.slice(0, 50));
  };

  const handleNumeroCelular = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange('numeroCelular', e.target.value.replaceAll(/\D/g, '').slice(0, 12));
  };

  const handleCodigoPostal = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange('codigoPostal', e.target.value.replaceAll(/\D/g, '').slice(0, 6));
  };

  const handleColonia = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange('colonia', e.target.value.replaceAll(ALPHANUMERIC_RE, '').slice(0, 65));
  };

  const handleDireccion = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange('direccion', e.target.value.replaceAll(ALPHANUMERIC_RE, '').slice(0, 100));
  };

  const handleCorreo = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange('correo', e.target.value.trim());
  };

  const handleDetalleDireccion = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange('detalleDireccion', e.target.value.slice(0, 15));
  };

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {labels.titulo}
      </Typography>
      <Grid container spacing={2}>

        {/* Nombre */}
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth required
            label={labels.nombre}
            name="nombre"
            value={datos.nombre}
            onChange={handleNombre}
            onBlur={() => markTouched('nombre')}
            error={!!fieldError('nombre')}
            helperText={fieldError('nombre') || `${datos.nombre.length}/50`}
            slotProps={{ htmlInput: { maxLength: 50 } }}
          />
        </Grid>

        {/* Indicativo + Celular */}
        <Grid size={{ xs: 12 }}>
          <Grid container spacing={1}>
            <Grid size={{ xs: 4, sm: 3 }}>
              <IndicativoPaisField
                value={datos.indicativoPais}
                onChange={dialCode => onChange('indicativoPais', dialCode)}
                onBlur={() => markTouched('indicativoPais')}
                error={!!fieldError('indicativoPais')}
                helperText={fieldError('indicativoPais')}
              />
            </Grid>
            <Grid size={{ xs: 8, sm: 9 }}>
              <TextField
                fullWidth required
                label="Número celular"
                name="numeroCelular"
                value={datos.numeroCelular}
                onChange={handleNumeroCelular}
                onBlur={() => markTouched('numeroCelular')}
                error={!!fieldError('numeroCelular')}
                helperText={fieldError('numeroCelular') || `${datos.numeroCelular.length}/12`}
                inputMode="numeric"
                slotProps={{ htmlInput: { maxLength: 12 } }}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Código postal + Municipio */}
        <Grid size={{ xs: 6 }}>
          <TextField
            fullWidth required
            label="Código postal"
            name="codigoPostal"
            value={datos.codigoPostal}
            onChange={handleCodigoPostal}
            onBlur={() => markTouched('codigoPostal')}
            error={!!fieldError('codigoPostal')}
            helperText={fieldError('codigoPostal') ?? ''}
            inputMode="numeric"
            slotProps={{ htmlInput: { maxLength: 6 } }}
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <TextField
            fullWidth required
            label="Municipio"
            name="municipio"
            value={datos.municipio}
            slotProps={{
              input: {
                readOnly: true,
                endAdornment: isLoadingMunicipio ? <CircularProgress size={18} /> : null,
              },
            }}
          />
        </Grid>

        {/* Colonia */}
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth required
            label={labels.colonia}
            name="colonia"
            value={datos.colonia}
            onChange={handleColonia}
            onBlur={() => markTouched('colonia')}
            error={!!fieldError('colonia')}
            helperText={fieldError('colonia') || `${datos.colonia.length}/65`}
            slotProps={{ htmlInput: { maxLength: 65 } }}
          />
        </Grid>

        {/* Dirección */}
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth required
            label="Dirección"
            name="direccion"
            value={datos.direccion}
            onChange={handleDireccion}
            onBlur={() => markTouched('direccion')}
            error={!!fieldError('direccion')}
            helperText={fieldError('direccion') || `${datos.direccion.length}/100`}
            slotProps={{ htmlInput: { maxLength: 100 } }}
          />
        </Grid>

        {/* Detalle de dirección (opcional) */}
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Detalle de dirección"
            name="detalleDireccion"
            value={datos.detalleDireccion}
            onChange={handleDetalleDireccion}
            placeholder="Departamento, bloque, edificio, indicaciones"
            helperText={`${datos.detalleDireccion.length}/15`}
            slotProps={{
              htmlInput: { maxLength: 15 },
              inputLabel: { shrink: datos.detalleDireccion !== '' || undefined },
            }}
          />
        </Grid>

        {/* Correo */}
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth required
            label="Correo electrónico"
            name="correo"
            type="email"
            value={datos.correo}
            onChange={handleCorreo}
            onBlur={() => markTouched('correo')}
            error={!!fieldError('correo')}
            helperText={fieldError('correo') ?? ''}
          />
        </Grid>

      </Grid>
    </Box>
  );
};
